import { ManuallyAddedUser, WorkingPasscodeList } from '@/types/passcode-list.types';
import { Activity, Dispatch, SetStateAction, useState } from 'react';
import {
  GetUploadEndpoint,
  getUploadFilename,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import UploadLarge from '@ui/uploads/upload-large';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { PASSCODE_LIST_TYPE } from '@/types/registration.types';
import classNames from 'classnames';
import styles from './add-new-passcode-list-card.module.css';
import { GenerateExampleCsv, isValidEmail } from '../../utils';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EToastTypes } from '@ui/toast/toast.types';

type AddPasscodeListMembersProps = {
  setWorkingPasscodeList: Dispatch<SetStateAction<WorkingPasscodeList>>;
  bubbleFileName?: Dispatch<SetStateAction<string | null>>;
  workingPasscodeList: WorkingPasscodeList;
};

type ManuallyAddedUserParams = {
  title: string;
  columnClasses: string;
  requiredFields: string[];
};

const ManuallyAddedUserProps: Record<number, ManuallyAddedUserParams> = {
  [PASSCODE_LIST_TYPE.EMAILS_ONLY]: {
    title: 'Emails',
    columnClasses: 'three-col',
    requiredFields: ['email'],
  },
  [PASSCODE_LIST_TYPE.PASSCODES_ONLY]: {
    title: 'Passcodes',
    columnClasses: 'three-col',
    requiredFields: ['passcode'],
  },
  [PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES]: {
    title: 'Email & Passcode',
    columnClasses: 'four-col',
    requiredFields: ['email', 'passcode'],
  },
  [PASSCODE_LIST_TYPE.MAGIC_LINKS]: {
    title: 'Magic Links',
    columnClasses: 'three-col',
    requiredFields: ['email'],
  },
  [PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION]: {
    title: 'Magic Link Registration',
    columnClasses: 'three-col',
    requiredFields: ['email'],
  },
};

const AddPasscodeListMembers = ({
  setWorkingPasscodeList,
  workingPasscodeList,
  bubbleFileName,
}: AddPasscodeListMembersProps) => {
  const workingEvent = useAppSelector(getWorkingProject);

  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const hasDownload = !!workingPasscodeList?.url;

  const isWideEmailField = [
    PASSCODE_LIST_TYPE.EMAILS_ONLY,
    PASSCODE_LIST_TYPE.MAGIC_LINKS,
    PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION,
  ].includes(workingPasscodeList.type);

  async function onFile(file: File | FileList) {
    if (file instanceof File && workingEvent) {
      setLoading(true);
      setFileName(file.name);
      if (bubbleFileName) {
        bubbleFileName(file.name);
      }
      try {
        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );
          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          setWorkingPasscodeList((list: WorkingPasscodeList) => ({
            ...list,
            url: finalUrl,
          }));
        }
      } catch (e) {
        console.error(e);
        toastTrigger('Error attaching file', {
          description: 'There was an error attaching the file',
          type: EToastTypes.error,
        });
      } finally {
        setLoading(false);
      }
    }
  }

  const downloadCurrent = async () => {
    if (workingPasscodeList?.url) {
      try {
        const link = document.createElement('a');
        link.setAttribute('href', workingPasscodeList.url);
        link.setAttribute('download', workingPasscodeList.name || 'current-passcode-list.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error(e);
        toastTrigger('Error downloading', {
          description: 'There was an error downloading the current list',
          type: EToastTypes.error,
        });
      }
    }
  };

  function setManuallyAddedUserValue(index: number, key: 'email' | 'first_name' | 'last_name' | 'passcode') {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      setWorkingPasscodeList((workingPasscodeList) => {
        return {
          ...workingPasscodeList,
          manually_added_users: workingPasscodeList.manually_added_users.map((user, idx) => {
            if (idx !== index) return user;
            return {
              ...user,
              [key]: value,
            };
          }),
        };
      });
    };
  }

  function addManuallyAddedUser() {
    setWorkingPasscodeList((workingPasscodeList) => {
      return {
        ...workingPasscodeList,
        manually_added_users: [
          ...(workingPasscodeList?.manually_added_users || []),
          {
            email: '',
            first_name: '',
            last_name: '',
            passcode: '',
          },
        ],
      };
    });
  }

  function removeManuallyAddedUser(index: number) {
    return () => {
      setWorkingPasscodeList((workingPasscodeList) => {
        if (workingPasscodeList?.manually_added_users) {
          return {
            ...workingPasscodeList,
            manually_added_users: workingPasscodeList?.manually_added_users.filter(
              (user: ManuallyAddedUser, i: number) => i !== index
            ),
          };
        } else {
          return workingPasscodeList;
        }
      });
    };
  }

  function manualEmailIsValid(index: number) {
    const email = workingPasscodeList?.manually_added_users[index]?.email || '';
    if (anyFieldHasValue(index)) {
      return isValidEmail(email);
    } else {
      return true;
    }
  }

  function manualPasscodeIsValid(index: number) {
    if (
      [PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES, PASSCODE_LIST_TYPE.PASSCODES_ONLY].includes(workingPasscodeList.type) &&
      anyFieldHasValue(index)
    ) {
      const passcode = workingPasscodeList?.manually_added_users[index]?.passcode || '';
      return passcode.length > 0;
    } else {
      return true;
    }
  }

  function anyFieldHasValue(index: number) {
    const user = workingPasscodeList?.manually_added_users[index];

    if (user) {
      return Object.values(user).some((value) => value.length > 0);
    }

    return false;
  }

  function renderManuallyAddHeader(headerText: string, fieldType: string | null, className: string | null) {
    return (
      <Container className={classNames('flex fd-row padding-l-14', styles[getStyleClass(className)])}>
        <Text fontSize="11px" color="var(--gray-400)">
          {headerText}
        </Text>
        <OptionalComponent
          display={
            fieldType !== null && ManuallyAddedUserProps[workingPasscodeList.type]?.requiredFields?.includes(fieldType)
          }
        >
          <Text
            className="padding-l-2"
            fontSize="11px"
            color="var(--gray-400)"
            style={{ position: 'relative', top: '-2px' }}
          >
            *
          </Text>
        </OptionalComponent>
      </Container>
    );
  }

  function getStyleClass(className: string | null) {
    // This handles three column and four column displays on the form.
    // A config class that is applied to the header and input that determines the width of both.
    // If one of the fields is an email and we are not on a 4 column view, the email will be 50% of the width
    // by applying a three-col-email class.
    // The remaining columns will be 25%. The 25% columns are marked with a class modifier of -other
    // by applying a three-col-other class.
    let styleClass = ManuallyAddedUserProps[workingPasscodeList.type]?.columnClasses;
    styleClass += isWideEmailField && className ? '-' + className : '';

    return styleClass;
  }

  return (
    <>
      <Container borderRadius="12px 12px 0 0" padding="0px 40px 24px 40px" height="200px">
        <Container className="padding-b-8">
          <Text color="var(--gray-400)" fontSize="11px">
            BULK UPLOAD
          </Text>
        </Container>
        <UploadLarge
          onFile={onFile}
          allowedFileTypes={['text/csv']}
          style={{
            height: '150px',
            minHeight: '150px',
            background: 'transparent',
            border: '1px dashed var(--gray-600)',
          }}
          uploading={loading}
          placeholder=""
          CustomTitle={
            <>
              <Container
                className="flex fd-column jc-center ai-center"
                height="150px"
                background="transparent"
                style={{
                  width: 'fit-content',
                  margin: 'auto',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  padding: '8px',
                  minHeight: '150px',
                }}
              >
                <Activity mode={fileName ? 'hidden' : 'visible'}>
                  <Text color={getThemeColorCSSVariable()} className="L2 flex fd-row jc-flex-start ai-center gap-8">
                    <Icon name={ICONS.ADD} size={16} />
                    <Text fontSize="16px">Upload an audience list</Text>
                  </Text>

                  <Text className="padding-t-4" fontSize="12px">
                    or drag and drop here file in .csv file format
                  </Text>
                </Activity>

                <Activity mode={fileName ? 'visible' : 'hidden'}>
                  <Text className="L2 flex fd-row jc-flex-start ai-center gap-8">
                    <Icon name={ICONS.UPLOAD} size={16} color={getThemeColorCSSVariable()} />
                    <Text fontSize="16px">{fileName}</Text>
                  </Text>

                  <Text className="padding-t-4" fontSize="12px" color="var(--gray-500)">
                    Click or drag and drop to replace
                  </Text>
                </Activity>
              </Container>
            </>
          }
        />
        <Container className="flex jc-space-between ai-center gap-8" width="100%" padding="20px 0">
          <Text fontSize={'11px'} width={'50%'} color="var(--gray-400)">
            Please utilize the template for best results
          </Text>
          <div className="flex ai-center gap-8">
            <OptionalComponent display={hasDownload}>
              <Button
                className="small no-style no-padding flex jc-flex-start ai-flex-start"
                onClick={downloadCurrent}
                design="LocalBttnSec"
                designSize="small"
              >
                <Icon name={ICONS.DOWNLOAD} size={12} />
                <Text className="padding-l-4" fontSize={'12px'}>
                  Current version
                </Text>
              </Button>
            </OptionalComponent>
            <Button
              onClick={() => GenerateExampleCsv(workingPasscodeList.type, workingEvent?.uuid)}
              className="flex jc-center ai-center"
              design="LocalBttnSec"
              designSize="small"
            >
              <Icon name={ICONS.DOWNLOAD} size={12} />
              <Text className="padding-l-4" fontSize={'12px'}>
                Download template
              </Text>
            </Button>
          </div>
        </Container>

        <Container>
          <Text color="var(--gray-400)" fontSize="11px">
            ADD MANUALLY
          </Text>
          <Container className="flex fd-column jc-center ai-flex-start gap-8" width="100%" padding="20px 0">
            <Container className="flex fd-row jc-center ai-flex-start gap-8" width="calc(100% - 20px)">
              <OptionalComponent display={workingPasscodeList.type !== PASSCODE_LIST_TYPE.PASSCODES_ONLY}>
                {renderManuallyAddHeader('Email', 'email', 'email')}
              </OptionalComponent>

              {renderManuallyAddHeader('First name', null, 'other')}

              {renderManuallyAddHeader('Last name', null, 'other')}

              <OptionalComponent
                display={
                  workingPasscodeList.type === PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES ||
                  workingPasscodeList.type === PASSCODE_LIST_TYPE.PASSCODES_ONLY
                }
              >
                {renderManuallyAddHeader('Passcode', 'passcode', 'other')}
              </OptionalComponent>
            </Container>
            {workingPasscodeList?.manually_added_users && (
              <>
                {workingPasscodeList.manually_added_users.map((user: ManuallyAddedUser, index: number) => (
                  <Container className="flex fd-row jc-center gap-8 margin-b-8" width="100%" key={index}>
                    <OptionalComponent display={workingPasscodeList.type !== PASSCODE_LIST_TYPE.PASSCODES_ONLY}>
                      <InputWithOverlay
                        inputHeight="40px"
                        onDebounce={(val) => {
                          setWorkingPasscodeList((prev) => ({
                            ...prev,
                            email: val,
                          }));
                        }}
                        wrapperClass={classNames(styles[getStyleClass('email') || ''])}
                        inputProps={{
                          className: 'S2',
                          placeholder: 'Email',
                          value: user.email,
                          onChange: setManuallyAddedUserValue(index, 'email'),
                          style: { fontSize: '11px' },
                        }}
                        status={!manualEmailIsValid(index) ? 'error' : undefined}
                      />
                    </OptionalComponent>

                    <InputWithOverlay
                      inputHeight="40px"
                      onDebounce={(val) => {
                        setWorkingPasscodeList((prev) => ({
                          ...prev,
                          first_name: val,
                        }));
                      }}
                      wrapperClass={classNames(styles[getStyleClass('other') || ''])}
                      inputProps={{
                        className: 'S2',
                        placeholder: 'First name',
                        value: user.first_name,
                        onChange: setManuallyAddedUserValue(index, 'first_name'),
                        style: { fontSize: '11px' },
                      }}
                    />

                    <InputWithOverlay
                      inputHeight="40px"
                      onDebounce={(val) => {
                        setWorkingPasscodeList((prev) => ({
                          ...prev,
                          last_name: val,
                        }));
                      }}
                      wrapperClass={classNames(styles[getStyleClass('other') || ''])}
                      inputProps={{
                        className: 'S2',
                        placeholder: 'Last name',
                        value: user.last_name,
                        onChange: setManuallyAddedUserValue(index, 'last_name'),
                        style: { fontSize: '11px' },
                      }}
                    />
                    <OptionalComponent
                      display={
                        workingPasscodeList.type === PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES ||
                        workingPasscodeList.type === PASSCODE_LIST_TYPE.PASSCODES_ONLY
                      }
                    >
                      <InputWithOverlay
                        inputHeight="40px"
                        onDebounce={(val) => {
                          setWorkingPasscodeList((prev) => ({
                            ...prev,
                            passcode: val,
                          }));
                        }}
                        wrapperClass={classNames(styles[getStyleClass('other') || ''])}
                        inputProps={{
                          className: 'S2',
                          placeholder: 'Passcode',
                          value: user.passcode,
                          onChange: setManuallyAddedUserValue(index, 'passcode'),
                          style: { fontSize: '11px' },
                        }}
                        status={!manualPasscodeIsValid(index) ? 'error' : undefined}
                      />
                    </OptionalComponent>
                    <OptionalComponent display={index !== 0}>
                      <Button className="clear" style={{ padding: 0 }} onClick={removeManuallyAddedUser(index)}>
                        <Icon name={ICONS.CLOSE} size={20} />
                      </Button>
                    </OptionalComponent>
                    <OptionalComponent display={index === 0}>
                      <Container width="20px"></Container>
                    </OptionalComponent>
                  </Container>
                ))}
              </>
            )}
            <Button
              className="flex fd-row jc-center ai-center padding-h-12 padding-v-8"
              onClick={addManuallyAddedUser}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
            >
              <Icon name={ICONS.ADD} size={11} />{' '}
              <Text fontSize="11px" className="padding-l-4">
                Add
              </Text>
            </Button>
          </Container>
        </Container>
      </Container>
    </>
  );
};

export default AddPasscodeListMembers;
